/*
  Equijump Ltd. · <aaa-newsletter-popup> - behaviour for
  sections/aaa-newsletter-popup.liquid. Extends the stock DialogComponent so
  open/close, scroll lock, Escape and click-outside all come from the theme.

  Auto-open rules (all must pass):
    1. Not already subscribed (logged-in customer, signed up via the popup on
       this browser, or arrived from a newsletter link - utm_source=shopify_email
       or utm_medium=email).
    2. Not dismissed within the last `data-reshow-days` (default 7).
    3. Cookie choice made (data-consent: answered | accepted | ignore). Uses
       Shopify's Customer Privacy API; regions with no banner pass straight away.
    4. This is at least page view `data-min-views` of the visit (sessionStorage).
    5. Then waits `data-delay` seconds.

  Analytics (Shopify custom events -> "Leads & contacts" pixel -> GA4, consented
  visitors only): newsletter_popup_view {trigger: auto|tab},
  newsletter_popup_close {stage: form|success}, newsletter_signup {source: popup}.
  All carry `path`. Editor previews never publish.

  Storage: localStorage "aaa-nlp" = { dismissedAt, subscribed } and
  sessionStorage "aaa-nlp-views". All access is wrapped - private browsing
  just means the popup behaves as a first visit.
*/
import { DialogComponent } from '@theme/dialog';

const STORE_KEY = 'aaa-nlp';
const VIEWS_KEY = 'aaa-nlp-views';
const DAY_MS = 24 * 60 * 60 * 1000;

function readStore() {
  try {
    return JSON.parse(window.localStorage.getItem(STORE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

function writeStore(patch) {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify({ ...readStore(), ...patch }));
  } catch {
    /* storage unavailable - nothing to persist */
  }
}

function countPageView() {
  try {
    const views = Number(window.sessionStorage.getItem(VIEWS_KEY) || 0) + 1;
    window.sessionStorage.setItem(VIEWS_KEY, String(views));
    return views;
  } catch {
    return 1;
  }
}

// Shopify web-pixel custom events -> the "Leads & contacts" custom pixel -> GA4.
// Retries briefly because Shopify.analytics can arrive after this module runs.
function publish(name, data, tries = 12) {
  const analytics = window.Shopify && window.Shopify.analytics;
  if (analytics && typeof analytics.publish === 'function') {
    try {
      analytics.publish(name, { path: window.location.pathname, ...data });
    } catch {
      /* never block the storefront */
    }
    return;
  }
  if (tries > 0) window.setTimeout(() => publish(name, data, tries - 1), 250);
}

function loadPrivacyApi() {
  return new Promise((resolve) => {
    const shopify = window.Shopify;
    if (shopify && shopify.customerPrivacy) return resolve(shopify.customerPrivacy);
    if (!shopify || typeof shopify.loadFeatures !== 'function') return resolve(null);
    try {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        resolve(error ? null : window.Shopify.customerPrivacy || null);
      });
    } catch {
      resolve(null);
    }
  });
}

class NewsletterPopup extends DialogComponent {
  #timer = null;
  #views = 0;
  #armed = false;
  #trigger = 'auto';

  connectedCallback() {
    super.connectedCallback();

    const store = readStore();
    this.subscribed = this.hasAttribute('data-subscribed') || store.subscribed === true;

    // Arrived from a newsletter link: they're already on the list.
    const params = new URLSearchParams(window.location.search);
    if (!this.subscribed && (params.get('utm_source') === 'shopify_email' || params.get('utm_medium') === 'email')) {
      this.subscribed = true;
      writeStore({ subscribed: true });
    }

    this.#wireForm();
    this.addEventListener('dialog:close', this.#onClose);
    this.addEventListener('dialog:open', this.#onOpen);
    this.refs.tab?.addEventListener('click', () => {
      this.#trigger = 'tab';
    });

    // Returning from Shopify's form post: show the success / error message.
    if (this.#handleFormReturn()) return;

    if (!this.subscribed && this.refs.tab) this.refs.tab.hidden = false;

    if (this.hasAttribute('data-design-mode')) {
      this.#wireEditor();
      return;
    }

    if (this.subscribed) return;

    this.#views = countPageView();
    this.#maybeArm();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#timer);
    document.removeEventListener('visitorConsentCollected', this.#onConsent);
    document.removeEventListener('shopify:section:select', this.#onEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.#onEditorDeselect);
    document.removeEventListener('shopify:block:select', this.#onEditorSelect);
  }

  get #minViews() {
    return Number(this.dataset.minViews) || 1;
  }

  get #delayMs() {
    return (Number(this.dataset.delay) || 0) * 1000;
  }

  get #reshowMs() {
    return (Number(this.dataset.reshowDays) || 7) * DAY_MS;
  }

  async #maybeArm() {
    if (this.#armed) return;

    const { dismissedAt } = readStore();
    if (dismissedAt && Date.now() - dismissedAt < this.#reshowMs) return;
    if (this.#views < this.#minViews) return;

    const consentOk = await this.#consentOk();
    if (!consentOk) {
      // Re-check once the visitor answers the banner on this page.
      document.addEventListener('visitorConsentCollected', this.#onConsent);
      return;
    }

    this.#armed = true;
    this.#timer = window.setTimeout(() => {
      if (!this.refs.dialog.open && !document.querySelector('dialog[open]')) this.showDialog();
    }, this.#delayMs);
  }

  #onConsent = () => {
    document.removeEventListener('visitorConsentCollected', this.#onConsent);
    // Give the banner a moment to close before anything else appears.
    window.setTimeout(() => this.#maybeArm(), 400);
  };

  async #consentOk() {
    const mode = this.dataset.consent || 'answered';
    if (mode === 'ignore') return true;

    const api = await loadPrivacyApi();
    if (!api) return true;

    try {
      if (typeof api.shouldShowBanner === 'function' && !api.shouldShowBanner()) return true;
      const consent = typeof api.currentVisitorConsent === 'function' ? api.currentVisitorConsent() : null;
      if (!consent) return true;

      if (mode === 'accepted') {
        return consent.analytics === 'yes' || consent.marketing === 'yes';
      }
      return ['analytics', 'marketing', 'preferences'].some((key) => consent[key] === 'yes' || consent[key] === 'no');
    } catch {
      return true;
    }
  }

  #onOpen = () => {
    if (this.hasAttribute('data-design-mode') || this.#trigger === 'result') return;
    publish('newsletter_popup_view', { trigger: this.#trigger });
  };

  #onClose = () => {
    if (this.hasAttribute('data-design-mode')) return;
    publish('newsletter_popup_close', { stage: this.dataset.state === 'success' ? 'success' : 'form' });
    this.#trigger = 'auto';
    if (!readStore().subscribed) writeStore({ dismissedAt: Date.now() });
  };

  #wireForm() {
    const form = this.querySelector('form');
    if (!form) return;

    let submitting = false;
    form.addEventListener('submit', (event) => {
      // One submit only - repeat clicks while Shopify responds trip its hCaptcha check.
      if (submitting) {
        event.preventDefault();
        return;
      }
      submitting = true;
      const button = form.querySelector('button[type="submit"], button:not([type])');
      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');
        button.style.pointerEvents = 'none';
        button.style.opacity = '0.6';
      }

      const tags = (this.dataset.tags || '').trim();
      if (tags && !form.querySelector('input[name="contact[tags]"]')) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'contact[tags]';
        input.value = tags;
        form.appendChild(input);
      }
      writeStore({ pendingAt: Date.now() });
    });
  }

  /** @returns {boolean} true if the popup was opened to show a form result */
  #handleFormReturn() {
    const success = this.querySelector('.email-signup__message .icon-success');
    const error = this.querySelector('[aria-invalid="true"]');
    if (!success && !error) return false;

    const { pendingAt } = readStore();
    const fromPopup = (pendingAt && Date.now() - pendingAt < 10 * 60 * 1000) || this.hasAttribute('data-design-mode');
    if (!fromPopup) return false;

    this.#trigger = 'result';
    if (success) {
      this.subscribed = true;
      writeStore({ subscribed: true, pendingAt: null });
      if (!this.hasAttribute('data-design-mode')) publish('newsletter_signup', { source: 'popup' });
      if (this.refs.tab) this.refs.tab.hidden = true;
      this.#showSuccess();
    } else {
      writeStore({ pendingAt: null });
      if (this.refs.tab) this.refs.tab.hidden = false;
    }
    this.showDialog();
    return true;
  }

  #showSuccess() {
    this.dataset.state = 'success';
    if (this.refs.success) this.refs.success.hidden = false;
    // Move focus to the confirmation (not the button, so no focus ring shows).
    window.setTimeout(() => this.refs.success?.focus({ preventScroll: true }), 300);
  }

  #wireEditor() {
    document.addEventListener('shopify:section:select', this.#onEditorSelect);
    document.addEventListener('shopify:block:select', this.#onEditorSelect);
    document.addEventListener('shopify:section:deselect', this.#onEditorDeselect);
  }

  #onEditorSelect = (event) => {
    if (event.detail && event.detail.sectionId === this.dataset.sectionId) this.showDialog();
  };

  #onEditorDeselect = (event) => {
    if (event.detail && event.detail.sectionId === this.dataset.sectionId) this.closeDialog();
  };
}

if (!customElements.get('aaa-newsletter-popup')) customElements.define('aaa-newsletter-popup', NewsletterPopup);
